import React, { useEffect } from 'react';
import Link from './Link';
import Spinner from './Spinner';
import InvoiceStatusBadge from './InvoiceStatusBadge';
import { fmtCurrency, fmtDate } from './format';
import { canTrack, getTrackingUrl } from './salesCalculations';
import { badgeStyle, closeButtonStyle, dialogStyle, infoGridStyle, labelStyle, overlayStyle, valueStyle } from './detailsStyles';
import { amountStyle, cardStyle, cellStyle, headRowStyle, headStyle, headTextStyle, linkStyle, stripeStyle, tableStyle, textCellStyle } from './styles';

const getFields = (invoice, onOpenOrder) => {
    const currency = (invoice.currency || '').trim().toUpperCase();
    const address = [invoice.deliveryName, invoice.deliveryAddress1, `${invoice.deliveryZipCode || ''} ${invoice.deliveryCity || ''}`.trim()]
        .filter(Boolean)
        .join(', ');

    return [
        ['Kund', [invoice.customerNumber, invoice.customerName].filter(Boolean).join(' - ')],
        ['Fakturadatum', fmtDate(invoice.invoiceDate)],
        ['Leveransdatum', fmtDate(invoice.deliveryDate)],
        ['Bokförd', invoice.booked ? 'Ja' : 'Nej'],
        ['Skickad', invoice.sent ? 'Ja' : 'Nej'],
        ['Vår referens', invoice.ourReference],
        ['Er referens', invoice.yourReference],
        ['Ert ordernummer', invoice.yourOrderNumber],
        ['Leveranssätt', invoice.wayOfDelivery],
        ['Leveransadress', address],
        ['Orderreferens', invoice.orderReference > 0
            ? <Link onClick={() => onOpenOrder(invoice.orderReference)}>{invoice.orderReference}</Link>
            : '-'],
        ['Postnox', canTrack(invoice) && (
            <a href={getTrackingUrl(invoice)} target="_blank" rel="noopener noreferrer" style={linkStyle}>Postnox</a>
        )],
        ['Valuta', invoice.currency],
        ['Växlingskurs i säljstatistiken', currency && currency !== 'SEK'
            ? `1 ${currency} = ${invoice.salesExchangeRate.toLocaleString('sv-SE', { maximumFractionDigits: 10 })} SEK`
            : null],
    ].filter(([, value]) => value);
};

const getTotals = (invoice) => [
    ['Netto', invoice.net],
    ['Frakt', invoice.freight],
    ['Moms', invoice.totalVAT],
    ['Totalt', invoice.total],
];

// suspended is true while an order dialog is open on top, so Escape only closes the topmost dialog
const InvoiceDetails = ({ invoice, refreshing, suspended, onOpenOrder, onClose }) => {
    useEffect(() => {
        if (suspended) return undefined;

        const onKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onClose, suspended]);

    return (
        <div style={overlayStyle} onClick={onClose}>
            <div
                role="dialog"
                aria-modal="true"
                aria-label={`Faktura ${invoice.documentNumber}`}
                style={dialogStyle}
                onClick={(e) => e.stopPropagation()}
            >
                <button type="button" aria-label="Stäng" onClick={onClose} style={closeButtonStyle}>
                    &times;
                </button>
                <h3 style={{ margin: 0 }}>
                    Faktura {invoice.documentNumber}
                    {invoice.isWeb && <span style={badgeStyle('#1779ba')}>Webb</span>}
                    <InvoiceStatusBadge invoice={invoice} />
                </h3>
                {refreshing && <p style={{ color: '#666', margin: '0.5rem 0 0' }}><Spinner />Uppdaterar faktura från Fortnox...</p>}

                <div style={infoGridStyle}>
                    {getFields(invoice, onOpenOrder).map(([label, value]) => (
                        <div key={label}>
                            <div style={labelStyle}>{label}</div>
                            <div style={valueStyle}>{value}</div>
                        </div>
                    ))}
                </div>

                <div style={cardStyle}>
                    <table style={tableStyle}>
                        <thead>
                            <tr style={headRowStyle}>
                                <th style={headTextStyle}>Artikelnr</th>
                                <th style={headTextStyle}>Beskrivning</th>
                                <th style={headStyle}>Antal</th>
                                <th style={headStyle}>Pris</th>
                                <th style={headStyle}>Summa</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(invoice.rows || []).map((row, i) => (
                                <tr key={i} style={stripeStyle(i)}>
                                    <td style={textCellStyle}>{row.articleNumber}</td>
                                    <td style={textCellStyle}>{row.description}</td>
                                    <td style={cellStyle}>{row.deliveredQuantity}</td>
                                    <td style={amountStyle(row.price)}>{fmtCurrency(row.price, invoice.currency)}</td>
                                    <td style={amountStyle(row.total)}>{fmtCurrency(row.total, invoice.currency)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <table style={{ width: 'auto', marginLeft: 'auto', marginTop: '1rem', fontSize: '0.85rem' }}>
                    <tbody>
                        {getTotals(invoice).map(([label, value], i, all) => {
                            const isLast = i === all.length - 1;
                            return (
                                <tr key={label} style={isLast ? { fontWeight: 'bold', borderTop: '2px solid #333' } : undefined}>
                                    <td style={{ ...textCellStyle, paddingRight: '2rem' }}>{label}</td>
                                    <td style={amountStyle(value)}>{fmtCurrency(value, invoice.currency)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default InvoiceDetails;
